import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import isEqual from "lodash/isEqual";
import {
  Check,
  Loader2,
  RefreshCw,
  Sparkles,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Badge, type StatusType } from "../../../../components/ui/badge";
import { Button } from "../../../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../components/ui/select";
import { Switch } from "../../../../components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../../../components/ui/tooltip";
import { useAllAutomations, useCurrentUser, useWorkspaceSkills } from "../../../../hooks/QueryHooks";
import { useBusinessContext } from "../../../../hooks/useBusinessContext";
import {
  createAutomation,
  readAutomation,
  triggerAutomationRun,
  updateAutomation,
} from "../../../../services/automation/automationEndpoints";
import { listComputedLabels } from "../../../../services/computedTraits/computedTraitsEndpoints";
import type { ComputedLabel } from "../../../../services/computedTraits/computedTraitsEndpoints.interface";
import {
  readHealthScoreSettings,
  updateHealthScoreSettings,
} from "../../../../services/healthScoreSettings/healthScoreSettingsEndpoints";
import type { HealthScoreSettings } from "../../../../services/healthScoreSettings/healthScoreSettingsEndpoints.interface";
import {
  createSkill,
  generateSkillDataQuery,
  updateSkill,
} from "../../../../services/skills/skillsEndpoints";
import { SkillKind, SkillStatus } from "../../../../services/skills/skillsEndpoints.interface";
import type { CustomSkill } from "../../../../services/skills/skillsEndpoints.interface";
import useWorkspaceStore from "../../../../store/workspace/workspaceStore";
import notify from "../../../../utils/notify";
import {
  HEALTH_SCORE_OUTPUT_SCHEMA,
  HEALTH_SCORE_SCHEDULE_PRESETS,
  buildHealthScoreAutomationName,
  buildHealthScoreModelFilters,
  buildHealthScoreSelectedFields,
  buildHealthScoreSkillInstructions,
  buildHealthScoreSkillName,
  getConfiguredHealthModels,
  getSchedulePresetValue,
} from "./healthScoreHelpers";
import {
  buildAvailableHealthScopes,
  buildDefaultHealthScoreSettings,
  encodeHealthScopeKeyForRoute,
  formatHealthTimestamp,
  hasUnsyncedHealthSettings,
  hydrateHealthScoreSettings,
  normalizeHealthScoreSettings,
} from "./healthScoreSettingsState";

const DEFAULT_SELECTED_FIELDS = [
  "accounts.name",
  "accounts.external_id",
  "accounts.avatar_url",
  "accounts.last_seen",
];

const DEFAULT_SORT = {
  field: "accounts.last_seen",
  order: "desc" as const,
  type: "property",
};

const getSchedulePresetLabel = (runSchedule: string) =>
  HEALTH_SCORE_SCHEDULE_PRESETS.find((preset) => preset.cron === runSchedule)?.label || runSchedule;

const getSyncStatus = ({
  settings,
  hasUnsavedChanges,
}: {
  settings: HealthScoreSettings;
  hasUnsavedChanges: boolean;
}): { label: string; status: StatusType } => {
  const configuredModels = getConfiguredHealthModels(settings);

  if (!configuredModels.length) {
    return { label: "No models configured", status: "info" };
  }

  if (hasUnsavedChanges) {
    return { label: "Unsaved changes", status: "warning" };
  }

  if (hasUnsyncedHealthSettings(settings)) {
    return { label: "Changes not synced", status: "warning" };
  }

  if (settings.linked_skill_id && settings.linked_automation_id && settings.last_synced_at) {
    return {
      label: `Synced ${formatHealthTimestamp(settings.last_synced_at)}`,
      status: "success",
    };
  }

  return { label: "Not synced", status: "pending" };
};

const HealthScoreSettingsPage = () => {
  const { workspaceId: workspaceSlugParam } = useParams();
  const workspace = useWorkspaceStore((state) => state.workspace);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [draft, setDraft] = useState<HealthScoreSettings | null>(null);
  const [schedulePreset, setSchedulePreset] = useState("weekly");

  const workspaceId = workspace?.id;
  const workspaceTimezone = workspace?.workspace_time_zone || "UTC";
  const workspaceSlug = workspace?.unique_identifier || workspaceSlugParam || "";

  const { data: user } = useCurrentUser({
    enabled: true,
  });

  const businessContextQuery = useBusinessContext(workspaceId || "");

  const settingsQuery = useQuery({
    queryKey: ["health-score-settings", workspaceId],
    queryFn: async () => {
      const response = await readHealthScoreSettings({ workspace_id: workspaceId! });
      return response.data;
    },
    enabled: !!workspaceId,
    staleTime: 1000 * 30,
    refetchOnWindowFocus: false,
  });

  const computedLabelsQuery = useQuery({
    queryKey: ["computed-labels", workspaceId],
    queryFn: async () => {
      const response = await listComputedLabels({ workspace_id: workspaceId! });
      return response.data as ComputedLabel[];
    },
    enabled: !!workspaceId,
    staleTime: 1000 * 60,
  });

  const skillsQuery = useWorkspaceSkills({
    workspaceId,
    enabled: !!workspaceId,
  });
  const skills = (skillsQuery.data || []) as CustomSkill[];

  const automationsQuery = useAllAutomations({
    workspaceId: workspaceId || "",
    automationType: "health_signal",
    enabled: !!workspaceId,
  });
  const automations = (automationsQuery.data || []) as any[];

  const settingsMutation = useMutation({
    mutationFn: async (payload: HealthScoreSettings) =>
      updateHealthScoreSettings({
        workspace_id: workspaceId!,
        payload,
      }),
  });

  const savedSettings = useMemo(
    () => hydrateHealthScoreSettings(settingsQuery.data || buildDefaultHealthScoreSettings()),
    [settingsQuery.data],
  );

  useEffect(() => {
    if (!settingsQuery.data) return;
    const hydrated = hydrateHealthScoreSettings(settingsQuery.data);
    setDraft(hydrated);
    setSchedulePreset(getSchedulePresetValue(hydrated.run_schedule));
  }, [settingsQuery.data]);

  const currentDraft = draft || savedSettings;
  const normalizedDraft = useMemo(
    () => normalizeHealthScoreSettings(currentDraft),
    [currentDraft],
  );
  const normalizedSavedSettings = useMemo(
    () => normalizeHealthScoreSettings(savedSettings),
    [savedSettings],
  );
  const hasUnsavedChanges = useMemo(
    () => !isEqual(normalizedDraft, normalizedSavedSettings),
    [normalizedDraft, normalizedSavedSettings],
  );

  const availableScopes = useMemo(
    () =>
      buildAvailableHealthScopes({
        businessContext: businessContextQuery.data,
        labels: computedLabelsQuery.data || [],
        savedModels: currentDraft.models,
      }),
    [businessContextQuery.data, computedLabelsQuery.data, currentDraft.models],
  );

  const configuredModels = useMemo(
    () => getConfiguredHealthModels(normalizedDraft),
    [normalizedDraft],
  );

  const syncStatus = useMemo(
    () => getSyncStatus({ settings: normalizedDraft, hasUnsavedChanges }),
    [hasUnsavedChanges, normalizedDraft],
  );

  const triggerHealthRunMutation = useMutation({
    mutationFn: async (scopeKey: string) => {
      const ownerId = (user as any)?.user_id as string | undefined;
      if (!workspaceId || !ownerId) throw new Error("User or workspace unavailable");

      const latestSettings = normalizedDraft;

      // Find the model matching the scopeKey
      const targetModel = latestSettings.models.find((m) => m.scope_key === scopeKey);
      if (!targetModel) throw new Error(`No model found for scope key: ${scopeKey}`);

      // Sync skill instructions before running so the agent uses the latest version
      const skillName = buildHealthScoreSkillName(ownerId);
      const existingSkill = skills.find((s) => s.id === latestSettings.linked_skill_id)
        || skills.find((s) => s.skill_name === skillName);

      const skillPayload = {
        workspace_id: workspaceId,
        name: skillName,
        display_name: "Ora Health Models",
        description: "Document-backed account health scoring instructions for Ora.",
        kind: SkillKind.CAPABILITY,
        instructions_content: buildHealthScoreSkillInstructions(latestSettings),
        priority: 5,
        keywords: ["health", "account health", "health model", "ora"],
        output_format: HEALTH_SCORE_OUTPUT_SCHEMA,
        output_format_type: "json_schema" as const,
        visibility: "protected" as const,
      };

      const skillResponse = existingSkill
        ? await updateSkill({
          ...skillPayload,
          skill_id: existingSkill.id,
          status: SkillStatus.ACTIVE,
          enabled: true,
        })
        : await createSkill(skillPayload);

      const syncedSkillId = (skillResponse.data as CustomSkill).id;

      // Use the model's automation_id if available
      let automationId = targetModel.automation_id;

      if (!automationId) {
        // Inline sync: create the automation for this model
        const modelAutomationName = buildHealthScoreAutomationName(ownerId, targetModel.scope_key);
        const existingAutomation = automations.find((a: any) => a.name === modelAutomationName);

        if (existingAutomation) {
          automationId = existingAutomation.id;
        } else {
          const automationResponse = await createAutomation({
            name: modelAutomationName,
            description: `Ora health scoring for ${targetModel.scope_label} model.`,
            trigger: "usage_based",
            entity_relation: "accounts",
            automation_type: "health_signal",
            is_precreated: false,
            filters: buildHealthScoreModelFilters(targetModel),
            metrics: [],
            status: latestSettings.schedule_enabled ? "active" : "paused",
            automation_actions: {
              suppression_period: "all_time",
              run_schedule: latestSettings.run_schedule,
              actions: [
                {
                  type: "run_skills",
                  skill_ids: [syncedSkillId],
                  skill_execution_mode: "per_entity",
                  skill_max_entities: 50,
                  skill_output_destination: "document",
                },
              ],
            },
            time_window: "all_time",
            selected_fields: buildHealthScoreSelectedFields(DEFAULT_SELECTED_FIELDS, latestSettings),
            time_zone: workspaceTimezone,
            sort: DEFAULT_SORT,
            owner_id: ownerId,
            workspace_id: workspaceId,
          });
          automationId = (automationResponse.data as any)?.id;
        }

        // Persist the automation_id back on the model
        const updatedModels = latestSettings.models.map((m) =>
          m.scope_key === scopeKey ? { ...m, automation_id: automationId } : m,
        );
        await settingsMutation.mutateAsync({
          ...latestSettings,
          models: updatedModels,
          linked_skill_id: syncedSkillId,
          last_synced_at: new Date().toISOString(),
        });
      } else {
        // Update existing automation with latest skill id
        await updateAutomation({
          workspace_id: workspaceId,
          automation_id: automationId,
          automation_actions: {
            suppression_period: "all_time",
            run_schedule: latestSettings.run_schedule,
            actions: [
              {
                type: "run_skills",
                skill_ids: [syncedSkillId],
                skill_execution_mode: "per_entity",
                skill_max_entities: 50,
                skill_output_destination: "document",
              },
            ],
          },
        });
      }

      // Now trigger the run — no need for health_model_scope_key since automation filters handle it
      return triggerAutomationRun({
        workspace_id: workspaceId,
        automation_id: automationId!,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["health-score-settings", workspaceId] });
      queryClient.invalidateQueries({ queryKey: ["skills", workspaceId] });
      queryClient.invalidateQueries({ queryKey: ["all_automations", workspaceId, "health_signal"] });
      notify({
        type: "success",
        heading: "Health scoring queued",
        messageList: ["Skill synced and health model will be evaluated against matching accounts shortly."],
      });
    },
    onError: (error: any) => {
      const detail = error?.response?.data?.detail || "Something went wrong";
      notify({
        type: "failure",
        heading: "Failed to run health scoring",
        messageList: [detail],
      });
    },
  });

  const updateDraft = (updater: (current: HealthScoreSettings) => HealthScoreSettings) => {
    setDraft((current) => updater(current || buildDefaultHealthScoreSettings()));
  };

  const handleScheduleToggle = (checked: boolean) => {
    if (checked && !configuredModels.length) {
      notify({
        type: "failure",
        heading: "Add a health model first",
        messageList: ["Create at least one health document before enabling scheduled scoring."],
      });
      return;
    }

    updateDraft((current) => ({
      ...current,
      schedule_enabled: checked,
    }));
  };

  const handleSave = async () => {
    if (!workspaceId || !draft || settingsMutation.isLoading) return;

    const ownerId = (user as any)?.user_id as string | undefined;
    if (!ownerId) {
      notify({
        type: "failure",
        heading: "User unavailable",
        messageList: ["Refresh the page and try again once your user profile has loaded."],
      });
      return;
    }

    if (!draft.run_schedule.trim()) {
      notify({
        type: "failure",
        heading: "Invalid schedule",
        messageList: ["Choose a valid schedule before syncing."],
      });
      return;
    }

    const timestamp = new Date().toISOString();
    const withTimestamps: HealthScoreSettings = {
      ...draft,
      updated_at: timestamp,
      models: draft.models.map((model) => ({
        ...model,
        updated_at: model.document_content.trim()
          ? model.updated_at || timestamp
          : model.updated_at || null,
      })),
    };
    const normalized = normalizeHealthScoreSettings(withTimestamps);

    if (!getConfiguredHealthModels(normalized).length) {
      notify({
        type: "failure",
        heading: "No health models configured",
        messageList: ["Create at least one health document before syncing."],
      });
      return;
    }

    let syncedSkillId = normalized.linked_skill_id || null;
    let syncedAutomationId = normalized.linked_automation_id || null;
    let latestSettings = normalized;

    try {
      const savedSettingsResponse = await settingsMutation.mutateAsync(normalized);
      latestSettings = hydrateHealthScoreSettings(savedSettingsResponse.data);

      const skillName = buildHealthScoreSkillName(ownerId);
      const automationName = buildHealthScoreAutomationName(ownerId);

      const existingSkill = skills.find((skill) => skill.id === latestSettings.linked_skill_id)
        || skills.find((skill) => skill.skill_name === skillName);

      const skillPayload = {
        workspace_id: workspaceId,
        name: skillName,
        display_name: "Ora Health Models",
        description: "Document-backed account health scoring instructions for Ora.",
        kind: SkillKind.CAPABILITY,
        instructions_content: buildHealthScoreSkillInstructions(latestSettings),
        priority: 5,
        keywords: ["health", "account health", "health model", "ora"],
        output_format: HEALTH_SCORE_OUTPUT_SCHEMA,
        output_format_type: "json_schema" as const,
        visibility: "protected" as const,
      };

      const skillResponse = existingSkill
        ? await updateSkill({
          ...skillPayload,
          skill_id: existingSkill.id,
          status: SkillStatus.ACTIVE,
          enabled: true,
        })
        : await createSkill(skillPayload);

      syncedSkillId = (skillResponse.data as CustomSkill).id;

      // Fire-and-forget: auto-generate headless data_query for the skill
      generateSkillDataQuery({
        workspace_id: workspaceId,
        skill_id: syncedSkillId,
      }).catch((err) => console.warn("generate-data-query failed (non-blocking):", err));

      // Create one automation per configured health model
      const modelsWithAutomations = [...latestSettings.models];
      const syncedConfiguredModels = getConfiguredHealthModels(latestSettings);
      // Track automations created in this sync loop to avoid duplicates
      // when the cached automations list is stale (e.g. rapid double-click)
      const createdInThisSync = new Map<string, string>(); // name → id

      for (const model of syncedConfiguredModels) {
        const modelAutomationName = buildHealthScoreAutomationName(ownerId, model.scope_key);

        // Check if we already created this automation in the current sync loop
        const justCreatedId = createdInThisSync.get(modelAutomationName);

        // Find existing automation by model's automation_id or by name
        const existingByModelId = model.automation_id
          ? automations.find((a: any) => a.id === model.automation_id)
          : null;
        const existingByName = automations.find((a: any) => a.name === modelAutomationName);
        const existingModelAutomation = existingByModelId || existingByName
          || (justCreatedId ? { id: justCreatedId } : null);

        // If not found in cache, try reading directly
        const linkedAutomationResponse = !existingModelAutomation && model.automation_id
          ? await readAutomation({
            workspace_id: workspaceId,
            automation_id: model.automation_id,
          }).catch(() => null)
          : null;
        const resolvedAutomation = existingModelAutomation || (linkedAutomationResponse?.data as any);

        const automationPayload = {
          name: modelAutomationName,
          description: `Ora health scoring for ${model.scope_label} model.`,
          trigger: "usage_based",
          entity_relation: "accounts",
          automation_type: "health_signal",
          is_precreated: false,
          filters: buildHealthScoreModelFilters(model),
          metrics: [],
          status: latestSettings.schedule_enabled ? "active" : "paused",
          automation_actions: {
            suppression_period: "all_time",
            run_schedule: latestSettings.run_schedule,
            actions: [
              {
                type: "run_skills",
                skill_ids: [syncedSkillId],
                skill_execution_mode: "per_entity",
                skill_max_entities: 50,
                skill_output_destination: "document",
              },
            ],
          },
          time_window: "all_time",
          selected_fields: buildHealthScoreSelectedFields(DEFAULT_SELECTED_FIELDS, latestSettings),
          time_zone: workspaceTimezone,
          sort: DEFAULT_SORT,
        };

        const automationResponse = resolvedAutomation
          ? await updateAutomation({
            workspace_id: workspaceId,
            automation_id: resolvedAutomation.id,
            ...automationPayload,
          })
          : await createAutomation({
            ...automationPayload,
            owner_id: ownerId,
            workspace_id: workspaceId,
          });

        const modelAutomationId = (automationResponse.data as any)?.id;
        if (modelAutomationId) {
          createdInThisSync.set(modelAutomationName, modelAutomationId);
        }

        // Store automation_id on the model
        const modelIdx = modelsWithAutomations.findIndex((m) => m.scope_key === model.scope_key);
        if (modelIdx !== -1) {
          modelsWithAutomations[modelIdx] = {
            ...modelsWithAutomations[modelIdx],
            automation_id: modelAutomationId,
          };
        }

        // Keep top-level linked_automation_id pointing to first automation for backward compat
        if (!syncedAutomationId) {
          syncedAutomationId = modelAutomationId;
        }
      }

      const finalPayload: HealthScoreSettings = {
        ...latestSettings,
        models: modelsWithAutomations,
        updated_at: timestamp,
        linked_skill_id: syncedSkillId,
        linked_automation_id: syncedAutomationId,
        last_synced_at: timestamp,
      };

      const finalResponse = await settingsMutation.mutateAsync(finalPayload);
      const hydrated = hydrateHealthScoreSettings(finalResponse.data);
      setDraft(hydrated);
      setSchedulePreset(getSchedulePresetValue(hydrated.run_schedule));

      notify({
        type: "success",
        heading: "Health scoring synced",
        messageList: ["Your health drafts, specialized health skill, and scheduler are now aligned."],
      });
    } catch (error: any) {
      const partialPayload: HealthScoreSettings = {
        ...latestSettings,
        linked_skill_id: syncedSkillId,
        linked_automation_id: syncedAutomationId,
      };

      if (syncedSkillId || syncedAutomationId) {
        try {
          const partialResponse = await settingsMutation.mutateAsync(partialPayload);
          setDraft(hydrateHealthScoreSettings(partialResponse.data));
        } catch {
          setDraft(hydrateHealthScoreSettings(partialPayload));
        }
      }

      const detail = typeof error?.response?.data?.detail === "string"
        ? error.response.data.detail
        : "The settings were saved, but syncing the health skill or automation failed.";

      notify({
        type: "failure",
        heading: "Sync incomplete",
        messageList: [detail],
      });
    } finally {
      queryClient.invalidateQueries({ queryKey: ["health-score-settings", workspaceId] });
      queryClient.invalidateQueries({ queryKey: ["skills", workspaceId] });
      queryClient.invalidateQueries({ queryKey: ["all_automations", workspaceId, "health_signal"] });
      queryClient.invalidateQueries({ queryKey: ["all_automations", workspaceId, "automation"] });
    }
  };

  if (settingsQuery.isError && !draft) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Health Scores</CardTitle>
          <CardDescription>We could not load your health settings right now.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (!draft) {
    return (
      <div className="flex min-h-[320px] items-center justify-center rounded-xl border border-border bg-card">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const saveButtonLabel = hasUnsavedChanges || hasUnsyncedHealthSettings(normalizedDraft)
    ? "Save & sync"
    : "Sync health scoring";

  return (
    <TooltipProvider>
      <div className="mx-auto max-w-5xl space-y-6 px-4 py-4">
        {/* Header */}
        <div className="space-y-1">
          <h2 className="text-xl font-semibold text-foreground">Health</h2>
          <p className="text-sm text-muted-foreground">
            Ora analyzes the accounts assigned to your relationships to generate health criteria for evaluating health.
            For best results, make sure accounts are assigned to your relationships first.
          </p>
        </div>

        {/* Sync bar */}
        <div className="flex items-center gap-3">
          <Badge variant="statusPill" status={syncStatus.status}>
            {syncStatus.label}
          </Badge>
          <Button
            onClick={() => void handleSave()}
            disabled={!configuredModels.length || settingsMutation.isLoading}
            variant="outline"
            size="sm"
          >
            {settingsMutation.isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Syncing
              </>
            ) : (
              <>
                <RefreshCw className="mr-2 h-4 w-4" />
                {saveButtonLabel}
              </>
            )}
          </Button>
        </div>

        {/* Health models table */}
        <div className="overflow-hidden rounded-lg border border-border">
          <div className="divide-y divide-border">
            {availableScopes.map((scope) => {
              const isConfigured = Boolean(scope.savedModel?.document_content?.trim());
              const destination = `/home/${workspaceSlug}/settings/health-score/model/${encodeHealthScopeKeyForRoute(scope.scope_key)}`;
              const canTrigger = isConfigured;
              const isTriggering =
                triggerHealthRunMutation.isLoading &&
                triggerHealthRunMutation.variables === scope.scope_key;

              return (
                <div
                  key={scope.scope_key}
                  className="flex items-center justify-between px-4 py-3"
                >
                  <div className="flex items-center gap-2">
                    <p className="text-sm text-foreground">{scope.scope_label}</p>
                    {scope.unavailable ? (
                      <Badge variant="outline" className="h-5 rounded-full px-2 text-[10px]">
                        Saved only
                      </Badge>
                    ) : null}
                  </div>
                  <div className="flex items-center gap-3">
                    {canTrigger && (
                      <button
                        type="button"
                        onClick={() => triggerHealthRunMutation.mutate(scope.scope_key)}
                        disabled={triggerHealthRunMutation.isLoading}
                        className="flex items-center gap-1.5 text-xs font-medium text-primary hover:underline disabled:opacity-50"
                      >
                        {isTriggering ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <RefreshCw className="h-3 w-3" />
                        )}
                        Run scoring
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => navigate(destination)}
                      className="text-sm font-medium text-primary hover:underline"
                    >
                      {isConfigured ? "Recreate health model" : "Create health model"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {availableScopes.length === 1 ? (
          <div className="rounded-lg border border-dashed border-border px-4 py-4">
            <p className="text-sm text-foreground">You only have the General fallback right now.</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Define segments in Settings &gt; Segments to create targeted health documents for
              specific account groups (e.g., Enterprise, Mid-Market).
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/home/${workspaceSlug}/settings/segments`)}
              >
                <Sparkles className="mr-2 h-4 w-4" />
                Open Segments
              </Button>
            </div>
          </div>
        ) : null}

        {/* Scheduled scoring */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Scheduled scoring</CardTitle>
            <CardDescription>
              This specialized health automation uses your workspace timezone ({workspaceTimezone}) and can create
              multiple health documents for the same account when stage and segment models overlap.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col gap-4 rounded-lg border border-border bg-muted/20 p-4 md:flex-row md:items-center md:justify-between">
              <div className="space-y-1">
                <p className="text-sm font-medium text-foreground">Enable scheduled scoring</p>
                <p className="text-xs text-muted-foreground">
                  Sync once after changing the schedule so the health automation stays in step with your settings.
                </p>
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <Switch
                      checked={draft.schedule_enabled}
                      onCheckedChange={handleScheduleToggle}
                      disabled={!configuredModels.length}
                    />
                  </div>
                </TooltipTrigger>
                {!configuredModels.length && (
                  <TooltipContent>No models configured</TooltipContent>
                )}
              </Tooltip>
            </div>

            <div className="space-y-2">
              <p className="text-[13px] text-foreground">Run cadence</p>
              <Select
                value={schedulePreset}
                onValueChange={(presetValue) => {
                  const preset = HEALTH_SCORE_SCHEDULE_PRESETS.find((item) => item.value === presetValue);
                  if (!preset) return;
                  setSchedulePreset(presetValue);
                  updateDraft((current) => ({
                    ...current,
                    run_schedule: preset.cron,
                    run_schedule_preset: preset.value,
                  }));
                }}
                disabled={!configuredModels.length}
              >
                <SelectTrigger className="h-9 w-full max-w-[320px] rounded-md text-[13px]">
                  <SelectValue placeholder="Select a schedule" />
                </SelectTrigger>
                <SelectContent>
                  {HEALTH_SCORE_SCHEDULE_PRESETS.map((preset) => (
                    <SelectItem key={preset.value} value={preset.value}>
                      {preset.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Valid cron: <span className="font-mono text-[11px]">{draft.run_schedule}</span>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </TooltipProvider>
  );
};

export default HealthScoreSettingsPage;
